/**
 * A `<harmony>` read back into a chord symbol.
 *
 * The kind names are the document's own, so a file written by anything that names kinds the
 * way the standard does is read straight. What needs care is everything else a writer may
 * do with the element.
 *
 * A file may say `kind="other"` -- or a kind this editor does not model, such as an altered
 * thirteenth -- and put the readable symbol in the `text` attribute, which is what several
 * commercial programs do. That text is read as though it had been typed in, so `C` with
 * `kind="other" text="m7b5"` comes back as a half-diminished chord rather than as nothing.
 * A symbol that survives neither route is dropped rather than guessed at: a chord chart with
 * a wrong chord in it is worse than one with a gap.
 *
 * `<offset>` moves the symbol forward from the reader's cursor, which is how a chord lands
 * on a beat in the middle of a note. `<inversion>` and `<degree>` are not read, because the
 * document does not model them.
 */

import { HARMONY_KINDS, parseChord } from "../document/harmony";
import type { ChordRoot, Harmony, HarmonyKind } from "../document/harmony";
import { createId } from "../document/ids";
import { STEPS } from "../document/pitch";
import type { Step } from "../document/pitch";
import { attr, child, numberOf, oneOf, textOf } from "../xml/read";
import type { XmlElement } from "../xml/parse";
import { fromDivisions } from "./common";

function rootOf(element: XmlElement | null, prefix: "root" | "bass"): ChordRoot | null {
  const node = child(element, prefix);
  if (node === null) return null;
  const step = textOf(node, `${prefix}-step`);
  if (step === null || !STEPS.includes(step as Step)) return null;
  return { step: step as Step, alter: numberOf(node, `${prefix}-alter`, 0) };
}

/** The kind, from the element's own name for it or from the symbol a writer printed. */
function kindOf(element: XmlElement, root: ChordRoot): HarmonyKind | null {
  const named = oneOf(element, "kind", HARMONY_KINDS, "major");
  if (textOf(element, "kind") === named) return named;

  const printed = attr(child(element, "kind"), "text");
  if (printed === null) return null;
  const read = parseChord(`${root.step}${printed}`);
  return typeof read === "string" ? null : read.kind;
}

/** One `<harmony>` at the reader's cursor, or `null` when it cannot be represented. */
export function readHarmony(
  element: XmlElement,
  divisions: number,
  cursor: number,
): Harmony | null {
  const root = rootOf(element, "root");
  if (root === null) return null;
  const kind = kindOf(element, root);
  if (kind === null) return null;
  const offset = fromDivisions(numberOf(element, "offset", 0), divisions);
  return {
    id: createId("harmony"),
    onset: Math.max(0, cursor + offset),
    root,
    kind,
    bass: rootOf(element, "bass"),
  };
}
